import type { FC } from 'react';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { BiData, type OpenStudioPresetsBiEvent } from '@wix/da-bi/pkg/events';
import StudioDraftIcon from '@wix/da-ds/pkg/Icons/24x24/StudioDraft';
import StudioScheduledIcon from '@wix/da-ds/pkg/Icons/24x24/StudioScheduled';
import StudioPublishedIcon from '@wix/da-ds/pkg/Icons/24x24/StudioPublished';
import StudioPlaygroundIcon from '@wix/da-ds/pkg/Icons/24x24/StudioPlayground';
import StudioStashIcon from '@wix/da-ds/pkg/Icons/24x24/StudioStash';
import StudioFolderIcon from '@wix/da-ds/pkg/Icons/24x24/Folder';
import StudioProductsIcon from '@wix/da-ds/pkg/Icons/24x24/StudioProducts';
import ApplicationsIcon from '@wix/da-ds/pkg/Icons/24x24/Applications';
import PresetIcon from '@wix/da-ds/pkg/Icons/24x24/Preset';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import { usePushModal } from '@wix/da-shared-react/pkg/utils/hooks/usePushModal';
import { StudioSection } from '../../../../types/store';
import {
  getActiveSection,
  getIsDiscountsSection,
  getIsShopSection,
} from '../../../../selectors/studio';
import { getHasApps } from '../../../../selectors/studio/apps';
import SimpleUploadButton from '../../../SimpleUploadButton';
import StorageCapacity from '../../../StorageCapacity';
import CreateFolderButton from '../../../CreateFolderButton';
import useBookmarksStream from '../../../../hooks/useBookmarksStream';
import CreateDiscountGhostButton from './CreateDiscountGhostButton';
import GalleryDisplaySettingsDropdown from '../../../_partials/GalleryDisplaySettingsDropdown';

import s from './DefaultLayout.scss';

export interface Props {
  activeSection: StudioSection;
  maxCapacity: number;
  freeCapacity: number;
  hasDeviations?: boolean;
  linkUploadToStudio?: boolean;
  onPresetsClick: () => void;
}

const DefaultLayout: FC<Props> = ({
  activeSection,
  maxCapacity,
  freeCapacity,
  linkUploadToStudio,
}) => {
  const { t } = useTranslation();
  // todo: any
  const headerButtons: any[] = [...useSectionLinks(), ...useActionListItems()];
  const showCreateFolderAction = activeSection === StudioSection.STASH;
  const isDiscountsSection = useSelector(getIsDiscountsSection);
  const showGallerySettingsDropdown =
    activeSection === StudioSection.PUBLISHED_GALLERIES;

  let showUploadButton = true;
  let showCreateDiscountButton = false;
  if (isDiscountsSection) {
    showUploadButton = false;
    showCreateDiscountButton = true;
  }
  if (activeSection === StudioSection.EARNINGS) {
    showUploadButton = false;
  }
  const folderType = 'stash';

  return (
    <div className={s['root']}>
      <div className={s['main']}>
        <h1 className={s['title']}>{t('studio.headerMobile.myStudio')}</h1>

        <StorageCapacity
          className={s['storage']}
          maxCapacity={maxCapacity}
          freeCapacity={freeCapacity}
        />

        <div className={s['actions']}>
          {showCreateFolderAction && (
            <CreateFolderButton
              label={t('studio.header.createFolder')}
              folderType={folderType}
            />
          )}
          {showUploadButton && (
            <SimpleUploadButton
              className={s['upload']}
              buttonClassName={s['upload-button']}
              label={t('studio.header.upload')}
              linkToSubmit={linkUploadToStudio}
            />
          )}
          {showCreateDiscountButton && <CreateDiscountGhostButton />}
          {showGallerySettingsDropdown && (
            <GalleryDisplaySettingsDropdown iconSizeOverride="small" />
          )}
        </div>
      </div>

      <nav className={s['mobile-nav']}>
        {headerButtons.map(buttonData => {
          if (!buttonData) return null;

          const { key, href, onClick, label, isLabelBold, biData } = buttonData;
          const Icon = buttonData.prefixIcon;
          return (
            <PlainButtonOrLink
              className={cn(
                s['mobile-nav-button'],
                'ds-card',
                isLabelBold && 'ds-card-selected'
              )}
              key={key}
              href={href}
              onClick={onClick}
              biData={biData}
            >
              <Icon disableDefaultColors />
              <span>{label}</span>
            </PlainButtonOrLink>
          );
        })}
      </nav>
    </div>
  );
};
DefaultLayout.displayName = 'DefaultLayout';

export default DefaultLayout;

// todo: combine next two with same in StudioNavigationDesktop after playground fallout
function useSectionLinks() {
  const { t } = useTranslation();
  const activeSection = useSelector(getActiveSection);
  const isShopSection = useSelector(getIsShopSection);
  const { items: bookmarks = [] } = useBookmarksStream();
  const hasApps = useSelector(getHasApps);

  return [
    {
      key: StudioSection.DRAFTS,
      label: t('studio.navigation.tabs.drafts'),
      href: Url.studio(),
      prefixIcon: StudioDraftIcon,
      isLabelBold: [StudioSection.DRAFTS, StudioSection.LITERATURE].includes(
        activeSection
      ),
    },
    {
      key: StudioSection.SCHEDULED,
      label: t('studio.navigation.tabs.scheduled'),
      href: Url.studio({ section: 'scheduled' }),
      prefixIcon: StudioScheduledIcon,
      isLabelBold: activeSection === StudioSection.SCHEDULED,
    },
    {
      key: StudioSection.PUBLISHED,
      label: t('studio.navigation.tabs.published'),
      href: Url.studio({ section: 'published' }),
      prefixIcon: StudioPublishedIcon,
      isLabelBold: [
        StudioSection.PUBLISHED,
        StudioSection.PUBLISHED_GALLERIES,
        StudioSection.PUBLISHED_TIERS,
      ].includes(activeSection),
    },
    {
      key: StudioSection.STASH,
      label: t('studio.navigation.tabs.stash'),
      href: Url.studioStash(),
      prefixIcon: StudioStashIcon,
      isLabelBold: activeSection === StudioSection.STASH,
    },
    hasApps && {
      key: StudioSection.APPS,
      label: t('studio.navigation.tabs.apps'),
      href: Url.studio({ section: 'apps' }),
      prefixIcon: ApplicationsIcon,
      isLabelBold: [StudioSection.APPS, StudioSection.APPS_UNLISTED].includes(
        activeSection
      ),
    },
    {
      key: StudioSection.PRODUCTS_LOBBY,
      label: t('studio.navigation.tabs.shop.mobile'),
      href: Url.studio({ products: 'root' }),
      prefixIcon: StudioProductsIcon,
      isLabelBold: isShopSection,
    },
    {
      key: StudioSection.PLAYGROUND,
      label: t('studio.navigation.tabs.playground'),
      href: `${Url.studio()}/playground`,
      prefixIcon: StudioPlaygroundIcon,
      isLabelBold: activeSection === StudioSection.PLAYGROUND,
    },
    bookmarks.length && {
      key: StudioSection.BOOKMARKS,
      label: t('studio.navigation.tabs.bookmarks'),
      href: `${Url.studio()}/shortcuts`,
      prefixIcon: StudioFolderIcon,
      isLabelBold: activeSection === StudioSection.BOOKMARKS,
    },
  ];
}

function useActionListItems() {
  const { t } = useTranslation();
  const showPresetsModal = usePushModal(ModalType.STUDIO_PRESETS);

  return [
    {
      key: 'settings',
      label: t('studio.header.settings'),
      onClick: showPresetsModal,
      prefixIcon: PresetIcon,
      biData: BiData<OpenStudioPresetsBiEvent>({
        evid: 19,
      }),
    },
  ];
}
